import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, linkWithCredential } from 'firebase/auth';
import { useTheme } from '../ThemeContext';
import SpeedsplitLogo from './ui/SpeedsplitLogo';
import { Sketch } from '@uiw/react-color';

const Navbar = () => {
  const [user] = useAuthState(auth);
  const { activeTheme, switchTheme, applyCustom, customColors, themes } = useTheme();
  const [fullName, setFullName] = useState('');
  const [isGoogleOnly, setIsGoogleOnly] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [sessionTimer, setSessionTimer] = useState(60);
  const [remainingTime, setRemainingTime] = useState(60);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [localPrimary, setLocalPrimary] = useState(customColors?.primary || '#8b5cf6');
  const [localAccent, setLocalAccent] = useState(customColors?.accent || '#a78bfa');
  const [localGradient, setLocalGradient] = useState(customColors?.useGradient || false);
  const [pickerTarget, setPickerTarget] = useState('primary');
  const customPickerRef = useRef(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  const navbarCollapseRef = useRef(null);
  const dropdownRef = useRef(null);
  const sessionTimeoutRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const sessionStartTimeRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (customPickerRef.current && !customPickerRef.current.contains(e.target)) {
        setShowCustomPicker(false);
      }
    };
    if (showCustomPicker) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCustomPicker]);

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const updateRemainingTime = useCallback(() => {
    if (sessionStartTimeRef.current) {
      const elapsed = Math.floor((Date.now() - sessionStartTimeRef.current) / (1000 * 60));
      const remaining = Math.max(0, sessionTimer - elapsed);
      setRemainingTime(remaining);
      
      if (remaining <= 0) {
        if (user) {
          auth.signOut()
            .then(() => {
              navigate('/');
              alert("You have been logged out due to session expiration.");
            })
            .catch(error => {
              console.error("Error logging out: ", error);
            });
        }
      }
    }
  }, [sessionTimer, user, navigate]);

  const startNewSession = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }
    
    sessionStartTimeRef.current = Date.now();
    setRemainingTime(sessionTimer);
    
    sessionTimeoutRef.current = setTimeout(() => {
      if (user) {
        auth.signOut()
          .then(() => {
            navigate('/');
            alert("You have been logged out due to session expiration.");
          })
          .catch(error => {
            console.error("Error logging out: ", error);
          });
      }
    }, sessionTimer * 60 * 1000); 
    
    countdownTimerRef.current = setInterval(updateRemainingTime, 60000);
    
    return true;
  }, [user, navigate, sessionTimer, updateRemainingTime]);

  useEffect(() => {
    if (user) {
      setIsGoogleOnly(
        user.providerData.length > 0 &&
        user.providerData.every((p) => p.providerId === 'google.com')
      );
    } else {
      setIsGoogleOnly(false);
    }
  }, [user]);

  useEffect(() => {
    const fetchFullName = async () => {
      if (user) {
        setFullName(user.displayName || '');
        try {
          const userQuery = query(collection(db, 'users'), where('uid', '==', user.uid));
          const querySnapshot = await getDocs(userQuery);
          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            setFullName(userData.fullName || user.displayName || '');
          }
        } catch (error) {
          console.error("Error fetching user full name: ", error);
        }
      }
    };

    fetchFullName();
    const onProfileUpdated = () => {
      void fetchFullName();
    };
    window.addEventListener('speedsplit-profile-updated', onProfileUpdated);
    return () => {
      window.removeEventListener('speedsplit-profile-updated', onProfileUpdated);
    };
  }, [user]);
  
  useEffect(() => {
    if (!user) {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      return;
    }
    
    if (!sessionStartTimeRef.current) {
      startNewSession();
    }
    
    return () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, [user, startNewSession]);

  useEffect(() => {
    const interval = setInterval(updateRemainingTime, 60000);
    return () => clearInterval(interval);
  }, [updateRemainingTime]);

  const handleLogout = async () => {
    try {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
      sessionStartTimeRef.current = null;
      await auth.signOut();
      navigate('/');
    } catch (error) {
      console.error("Error logging out: ", error);
    }
  };

  const handleRefreshSession = () => {
    startNewSession();
    setRefreshSuccess(true);
    setTimeout(() => {
      setRefreshSuccess(false);
      setShowRefreshModal(false);
    }, 2000);
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setIsChangingPassword(true);

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords don't match.");
      setIsChangingPassword(false);
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters.");
      setIsChangingPassword(false);
      return;
    }

    try {
      if (isGoogleOnly) {
        const credential = EmailAuthProvider.credential(user.email, newPassword);
        await linkWithCredential(user, credential);
        setIsGoogleOnly(false);
      } else {
        if (!currentPassword) {
          setPasswordError("Please enter your current password.");
          setIsChangingPassword(false);
          return;
        }
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
        await updatePassword(user, newPassword);
      }

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        setShowPasswordModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setIsChangingPassword(false);
      }, 2000);

    } catch (error) {
      console.error("Error changing password:", error);

      switch (error.code) {
        case 'auth/wrong-password':
          setPasswordError("Current password is incorrect.");
          break;
        case 'auth/credential-already-in-use':
        case 'auth/email-already-in-use':
          setPasswordError("A password is already set for this account. Please use Change Password instead.");
          break;
        case 'auth/weak-password':
          setPasswordError("Password is too weak. Please choose a stronger password.");
          break;
        case 'auth/requires-recent-login':
          setPasswordError("Please log out and log back in, then try again.");
          break;
        default:
          setPasswordError("Failed to update password. Please try again.");
      }
      setIsChangingPassword(false);
    }
  };

  const handleLinkClick = (e) => {
    if (e && e.target && e.target.classList.contains('dropdown-toggle')) {
      return;
    }
    
    if (navbarCollapseRef.current && window.innerWidth < 992) {
      navbarCollapseRef.current.classList.remove('show');
    }
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation(); 
    setDropdownOpen(!dropdownOpen);
  };

  const toggleNavbar = (e) => {
    if (e) {
      e.stopPropagation(); 
    }
    if (navbarCollapseRef.current) {
      navbarCollapseRef.current.classList.toggle('show');
    }
  };

  const handleClickOutside = (event) => {
    const isNavbarTarget = navbarCollapseRef.current && 
                          !navbarCollapseRef.current.contains(event.target) && 
                          !event.target.closest('.navbar-toggler');
    
    if (isNavbarTarget && window.innerWidth < 992) {
      navbarCollapseRef.current.classList.remove('show');
    }
    
    const isDropdownTarget = dropdownRef.current && !dropdownRef.current.contains(event.target);
    if (isDropdownTarget) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    
    const navLinks = document.querySelectorAll('.navbar-nav .nav-link:not(.dropdown-toggle)');
    navLinks.forEach(link => {
      link.addEventListener('click', handleLinkClick);
    });
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      navLinks.forEach(link => {
        link.removeEventListener('click', handleLinkClick);
      });
    };
  }, []);

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-dark">
        <div className="container-fluid">
          <Link className="navbar-brand d-flex align-items-center" to="/">
            <SpeedsplitLogo height={36} />
          </Link>
          <button 
            className="navbar-toggler" 
            type="button" 
            onClick={(e) => toggleNavbar(e)}
            aria-controls="navbarNav" 
            aria-expanded="false" 
            aria-label="Toggle navigation">
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarNav" ref={navbarCollapseRef}>
            <ul className="navbar-nav ms-auto">
              <li className="nav-item d-flex align-items-center">
                <Link className="nav-link" to="/" onClick={(e) => handleLinkClick(e)}>Home</Link>
              </li>
              <li className="nav-item d-flex align-items-center">
                <Link className="nav-link" to="/help" onClick={(e) => handleLinkClick(e)}>Help</Link>
              </li>
              <li className="nav-item d-flex align-items-center">
                <Link className="nav-link" to="/contact-us" onClick={(e) => handleLinkClick(e)}>Contact Us</Link>
              </li>
              {!user && (
                <li className="nav-item d-flex align-items-center">
                  <Link className="nav-link" to="/signup" onClick={(e) => handleLinkClick(e)}>Sign Up</Link>
                </li>
              )}
              {!user ? (
                <li className="nav-item d-flex align-items-center">
                  <Link className="nav-link" to="/login" onClick={(e) => handleLinkClick(e)}>Login</Link>
                </li>
              ) : (
                <li className="nav-item dropdown" ref={dropdownRef}>
                  <button 
                    className="nav-link dropdown-toggle d-flex align-items-center border-0 bg-transparent" 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDropdown(e);
                    }}
                    aria-expanded={dropdownOpen}
                  >
                    <div className="user-avatar me-2">
                      <span className="avatar-circle">
                        {fullName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span>Hello, {fullName}</span>
                  </button>
                  <ul className={`dropdown-menu dropdown-menu-end ${dropdownOpen ? 'show' : ''}`} 
                    style={{right: '0'}}>
                    <li>
                      <div className="dropdown-header">
                        <strong>User Options</strong>
                      </div>
                    </li>
                    <li><hr className="dropdown-divider" style={{borderColor: 'var(--glass-border)'}} /></li>
                    <li>
                      <Link className="dropdown-item" to="/profile" onClick={() => setDropdownOpen(false)}>
                        <i className="bi bi-person-lines-fill me-2"></i> My profile
                      </Link>
                    </li>
                    <li>
                      <Link className="dropdown-item" to="/my-requests" onClick={() => setDropdownOpen(false)}>
                        <i className="bi bi-inbox me-2"></i> My requests
                      </Link>
                    </li>
                    <li>
                      <button className="dropdown-item" onClick={() => {setShowPasswordModal(true); setDropdownOpen(false);}}>
                        <i className="bi bi-key me-2"></i> Change Password
                      </button>
                    </li>
                    <li>
                      <button className="dropdown-item" onClick={() => {setShowRefreshModal(true); setDropdownOpen(false);}}>
                        <i className="bi bi-arrow-clockwise me-2"></i> 
                        Refresh Session
                        <small className="ms-2 text-muted">({formatTime(remainingTime)} left)</small>
                      </button>
                    </li>
                    <li><hr className="dropdown-divider" style={{borderColor: 'var(--glass-border)'}} /></li>
                    <li>
                      <button className="dropdown-item text-danger" onClick={() => {handleLogout(); setDropdownOpen(false); handleLinkClick();}}>
                        <i className="bi bi-box-arrow-right me-2"></i> Logout
                      </button>
                    </li>
                  </ul>
                </li>
              )}
              <li className="nav-item d-flex align-items-center ms-2">
                <div className="theme-switcher" role="group" aria-label="Select theme">
                  {Object.entries(themes).map(([key, theme]) => (
                    <button
                      key={key}
                      className={`theme-dot${activeTheme === key ? ' active' : ''}`}
                      style={{ backgroundColor: theme.primary }}
                      onClick={() => { switchTheme(key); handleLinkClick(); }}
                      title={`${theme.emoji} ${theme.name}`}
                      aria-label={`Switch to ${theme.name} theme`}
                      aria-pressed={activeTheme === key}
                    />
                  ))}
                  <div className="position-relative" ref={customPickerRef}>
                    <button
                      className={`theme-dot theme-dot-custom${activeTheme === 'custom' ? ' active' : ''}`}
                      onClick={() => setShowCustomPicker(p => !p)}
                      title="Custom colour"
                      aria-label="Open custom colour picker"
                      aria-pressed={activeTheme === 'custom'}
                      style={activeTheme === 'custom'
                        ? { background: localGradient ? `linear-gradient(135deg,${localPrimary},${localAccent})` : localPrimary }
                        : {}}
                    >
                      {activeTheme !== 'custom' && <i className="bi bi-palette" style={{ fontSize: '9px', lineHeight: 1, color: '#fff', pointerEvents: 'none' }}></i>}
                    </button>
                    {showCustomPicker && (
                      <div
                        className="theme-custom-popover card shadow-lg"
                        style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 1060, minWidth: 0, overflow: 'hidden', width: 'auto' }}
                      >
                        <div className="px-3 pt-2 pb-1" style={{ borderBottom: '1px solid var(--glass-border)' }}>
                          <span className="small fw-semibold" style={{ color: 'var(--text-primary)' }}>
                            <i className="bi bi-palette me-1"></i>Custom Theme
                          </span>
                        </div>

                        <div className="px-3 pt-2 pb-1 d-flex align-items-center gap-2" style={{ borderBottom: '1px solid var(--glass-border)' }}>
                          <button
                            className={`btn btn-sm px-2 py-0 ${pickerTarget === 'primary' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ fontSize: '0.72rem', borderRadius: 6 }}
                            onClick={() => setPickerTarget('primary')}
                          >
                            Primary
                          </button>
                          <button
                            className={`btn btn-sm px-2 py-0 ${pickerTarget === 'accent' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={{ fontSize: '0.72rem', borderRadius: 6 }}
                            onClick={() => { setLocalGradient(true); setPickerTarget('accent'); }}
                          >
                            Accent
                          </button>
                          <div className="ms-auto d-flex align-items-center gap-1" style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                            <input
                              type="checkbox"
                              id="gradient-toggle"
                              className="form-check-input m-0"
                              style={{ width: 13, height: 13, cursor: 'pointer' }}
                              checked={localGradient}
                              onChange={e => {
                                e.stopPropagation();
                                setLocalGradient(e.target.checked);
                                if (e.target.checked) setPickerTarget('accent');
                              }}
                            />
                            <label htmlFor="gradient-toggle" style={{ cursor: 'pointer', marginBottom: 0 }}>Gradient</label>
                          </div>
                        </div>

                        <div onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
                          <Sketch
                            color={pickerTarget === 'primary' ? localPrimary : localAccent}
                            onChange={(c) => {
                              const hex = c.hex;
                              if (pickerTarget === 'primary') setLocalPrimary(hex);
                              else setLocalAccent(hex);
                            }}
                            style={{ boxShadow: 'none', background: 'transparent', width: 220 }}
                            presetColors={[
                              '#7c3aed','#0066cc','#10b981','#3b82f6','#f43f5e',
                              '#f97316','#eab308','#06b6d4','#8b5cf6','#ec4899',
                            ]}
                          />
                        </div>

                        <div className="px-3 pb-3">
                          <div
                            className="rounded mb-2"
                            style={{
                              height: 20,
                              background: localGradient
                                ? `linear-gradient(135deg,${localPrimary},${localAccent})`
                                : localPrimary,
                            }}
                          />
                          <button
                            className="btn btn-sm w-100 fw-semibold"
                            style={{ background: localPrimary, color: '#fff', border: 'none', fontSize: '0.8rem', borderRadius: 8 }}
                            onClick={() => { applyCustom(localPrimary, localAccent, localGradient); setShowCustomPicker(false); }}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </nav>
      
      {showPasswordModal && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{background: "linear-gradient(135deg, var(--primary), var(--accent))"}}>
                <h5 className="modal-title text-white">
                  <i className="bi bi-key me-2"></i>
                  {isGoogleOnly ? 'Set Password' : 'Change Password'}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowPasswordModal(false)}></button>
              </div>
              <div className="modal-body">
                {showSuccess ? (
                  <div className="alert alert-success" role="alert">
                    <i className="bi bi-check-circle me-2"></i>
                    {isGoogleOnly ? 'Password set successfully! You can now sign in with email & password.' : 'Password changed successfully!'}
                  </div>
                ) : (
                  <form>
                    {isGoogleOnly && (
                      <div className="alert alert-info py-2 mb-3 small">
                        <i className="bi bi-google me-2"></i>
                        You signed in with Google. Set a password to also enable email &amp; password sign-in.
                      </div>
                    )}
                    {passwordError && (
                      <div className="alert alert-danger">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {passwordError}
                      </div>
                    )}
                    {!isGoogleOnly && (
                      <div className="mb-3">
                        <label htmlFor="currentPassword" className="form-label">Current Password</label>
                        <input
                          type="password"
                          className="form-control"
                          id="currentPassword"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          required
                          disabled={isChangingPassword}
                        />
                      </div>
                    )}
                    <div className="mb-3">
                      <label htmlFor="newPassword" className="form-label">{isGoogleOnly ? 'Set Password' : 'New Password'}</label>
                      <input
                        type="password"
                        className="form-control"
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        disabled={isChangingPassword}
                      />
                      <div className="form-text">Password must be at least 6 characters long</div>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={isChangingPassword}
                      />
                    </div>
                  </form>
                )}
              </div>
              {!showSuccess && (
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)} disabled={isChangingPassword}>
                    <i className="bi bi-x me-2"></i>Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleChangePassword} disabled={isChangingPassword}>
                    {isChangingPassword ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {isGoogleOnly ? 'Setting Password...' : 'Changing Password...'}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check2 me-2"></i>{isGoogleOnly ? 'Set Password' : 'Change Password'}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showRefreshModal && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{background: "linear-gradient(135deg, var(--primary), var(--accent))"}}>
                <h5 className="modal-title text-white">
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Session Timer
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowRefreshModal(false)}></button>
              </div>
              <div className="modal-body">
                {refreshSuccess ? (
                  <div className="alert alert-success" role="alert">
                    <i className="bi bi-check-circle me-2"></i>
                    Session refreshed successfully!
                  </div>
                ) : (
                  <div>
                    <div className="alert alert-info" role="alert">
                      <i className="bi bi-clock me-2"></i>
                      <strong>Time Remaining:</strong> {formatTime(remainingTime)}
                    </div>
                    <p>Your session will automatically expire after the set duration. Click "Refresh Session" to restart the timer.</p>
                    <div className="mb-3">
                      <label htmlFor="sessionTimerInput" className="form-label">Session Duration (minutes)</label>
                      <input 
                        type="number" 
                        className="form-control" 
                        id="sessionTimerInput"
                        value={sessionTimer}
                        min="1"
                        max="120"
                        onChange={(e) => setSessionTimer(Math.min(120, Math.max(1, parseInt(e.target.value) || 60)))}
                      />
                      <div className="form-text">Session duration can be between 1 and 120 minutes</div>
                    </div>
                  </div>
                )}
              </div>
              {!refreshSuccess && (
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowRefreshModal(false)}>
                    <i className="bi bi-x me-2"></i>Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={handleRefreshSession}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>Refresh Session
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;